import type { CSSObject, Breakpoint } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

// ----------------------------------------------------------------------

const COMPANY_INFO = {
  name: '(주)빌드업랩스',
  ceo: '대표 정장현',
  businessNumber: '사업자등록번호 697-81-02704',
  onlineSalesNumber: '통신판매업신고번호 2025-서울영등포-0072',
  address: '주소 서울특별시 영등포구 경인로 775, 4동 419호',
};

const CONTACT_INFO = {
  inquiry: '문의',
  email: 'official@builduplabs.kr',
  phone: '대표전화',
  phoneNumber: '070.4138.0266',
};

/** 문의 칸 아래 링크 — 실시간 문의(채널톡 · 새 탭) · 사용 가이드(검색엔진도 이 링크로 가이드를 찾는다) */
const CONTACT_LINKS = [
  { name: '실시간 문의', href: 'https://catchmoments.channel.io', external: true },
  { name: '사용 가이드', href: paths.guide.root, external: false },
];

const LEGAL_LINKS = [
  { name: '서비스 이용약관', href: paths.home.terms.service },
  { name: '개인정보 처리 방침', href: paths.home.terms.privacy },
  { name: '마케팅 정보 활용 약관', href: paths.home.terms.marketing },
];

/** 칸 이름(「문의」 · 「대표전화」 · 「LEGAL」) */
const FOOTER_LABEL_SX = { fontWeight: 500, fontSize: '13.5px', lineHeight: '14px', color: '#929292' } as const;

/** 칸 안 링크 한 줄 — 문의 칸과 LEGAL 칸이 같은 글씨 */
const FOOTER_LINK_SX = {
  fontWeight: 500,
  fontSize: '13.5px',
  lineHeight: '32px',
  color: '#222222',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
} as const;

// ----------------------------------------------------------------------

// ⚠️ height/minHeight 의 반응형 객체는 styled 가 해석하지 못해 무효다 — 캐스트는 타입만 통과시킨다
const FooterRoot = styled('footer')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: { xs: '600px', md: '330px' },
  background: '#FFFFFF',
  minHeight: { xs: '600px', md: '330px' },
}) as unknown as CSSObject);

type HomeFooterProps = React.ComponentProps<typeof FooterRoot> & {
  layoutQuery?: Breakpoint;
};

export function HomeFooter({ sx, layoutQuery = 'md', ...other }: HomeFooterProps) {
  return (
    <FooterRoot sx={sx} {...other}>
      <Divider />

      {/* <Container
        sx={(theme) => ({
          pb: { xs: 3, md: 5 },
          pt: { xs: 0, md: 1 },
          textAlign: 'center',
          height: { xs: '85vh', md: '100%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          [theme.breakpoints.up(layoutQuery)]: { textAlign: 'unset' },
        })}
      > */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: { xs: 'flex-start', md: 'center' },
        alignItems: { xs: 'flex-start', md: 'flex-start' },
        width: '80%',
        maxWidth: '1240px',
        mx: 'auto',
        // height: { xs: '600px', md: '100%' },
        pt: 6,
        // pl: { xs: 0, md: 50 },
        gap: { xs: 4, md: 0 },
        position: 'relative'
      }}>
        {/* 회사 정보 섹션 */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          order: { xs: 3, md: 1 }, // xs에서는 마지막, md에서는 첫 번째
        }}>
          {/* 회사명 */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '15px',
              lineHeight: '24px',
              color: '#222222',
              top: { xs: '0px', md: 'auto' },
              left: { xs: '0px', md: 'auto' },
              // // width: { xs: '96px', md: 'auto' },
              // height: { xs: '24px', md: 'auto' },
            }}
          >
            {COMPANY_INFO.name}
          </Typography>

          {/* 대표 */}
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '13.5px',
              lineHeight: '14px',
              color: '#222222',
              top: { xs: '38px', md: 'auto' },
              left: { xs: '0px', md: 'auto' },
              // width: { xs: '64px', md: 'auto' },
              // height: { xs: '14px', md: 'auto' },
            }}
          >
            {COMPANY_INFO.ceo}
          </Typography>

          <Stack direction='row' spacing={2} flexWrap='wrap'>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '13.5px',
                lineHeight: '14px',
                color: '#222222',
                top: { xs: '0px', md: 'auto' },
                left: { xs: '-5px', md: 'auto' },
              }}
            >
              사업자등록번호
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '13.5px',
                lineHeight: '14px',
                color: '#222222',
                top: { xs: '0px', md: 'auto' },
                left: { xs: '0px', md: 'auto' },
              }}
            >
              697-81-02704
            </Typography>
          </Stack>

          <Stack direction='row' spacing={2} flexWrap='wrap'>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '13.5px',
                lineHeight: '14px',
                color: '#222222',
                top: { xs: '0px', md: 'auto' },
                left: { xs: '-10px', md: 'auto' },
              }}
            >통신판매업신고번호
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '13.5px',
                lineHeight: '14px',
                color: '#222222',
                top: { xs: '0px', md: 'auto' },
                left: { xs: '5px', md: 'auto' },
              }}
            >
              2025-서울영등포-0072
            </Typography>
          </Stack>

          <Stack direction='row' spacing={2} flexWrap='wrap'>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '13.5px',
                lineHeight: '14px',
                color: '#222222',
              }}
            >
              주소
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '13.5px',
                lineHeight: '14px',
                color: '#222222',
              }}
            >
              서울특별시 영등포구 경인로 775, 4동 419호
            </Typography>
          </Stack>
        </Box>

        <Box display={{ xs: 'none', md: 'block' }}
          flexGrow={{ xs: 0, md: 1 }}
          order={{ xs: 2, md: 2 }} />

        {/* 연락처 정보 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1, md: 2 },
            pr: { xs: 0, md: 4 },
            order: { xs: 1, md: 2 }, // xs에서는 첫 번째, md에서는 두 번째
          }}
        >
          {/* 문의 — 링크는 모두 LEGAL 링크와 같은 글씨 */}
          <Typography sx={FOOTER_LABEL_SX}>{CONTACT_INFO.inquiry}</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Link href={`mailto:${CONTACT_INFO.email}`} sx={FOOTER_LINK_SX}>
              {CONTACT_INFO.email}
            </Link>
            {CONTACT_LINKS.map((link) => (
              <Link
                key={link.name}
                {...(link.external ? { href: link.href, target: '_blank', rel: 'noopener' } : { component: RouterLink, href: link.href })}
                sx={FOOTER_LINK_SX}
              >
                {link.name}
              </Link>
            ))}
          </Box>

          <Typography sx={FOOTER_LABEL_SX}>{CONTACT_INFO.phone}</Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Link href={`tel:${CONTACT_INFO.phoneNumber.replace(/\./g, '')}`} sx={FOOTER_LINK_SX}>
              {CONTACT_INFO.phoneNumber}
            </Link>
          </Box>
        </Box>

        {/* 법적 정보 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1, md: 2 },
            order: { xs: 2, md: 3 }, // xs에서는 두 번째, md에서는 세 번째
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '13.5px',
              lineHeight: '14px',
              color: '#929292',
            }}
          >
            LEGAL
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              // gap: 1,
            }}
          >
            {LEGAL_LINKS.map((link) => (
              <Link
                key={link.name}
                component={RouterLink}
                href={link.href}
                sx={FOOTER_LINK_SX}
              >
                {link.name}
              </Link>
            ))}
          </Box>
        </Box>
      </Box>

      {/* 저작권 정보 */}
      <Box sx={{
        // mt: { xs: 6, md: 4 },
        width: '80%',
        maxWidth: '1240px',
        mx: 'auto',
        mt: 4,
        mb: 6
      }}>
        <Typography
          sx={{
            fontWeight: 300,
            fontSize: '13.5px',
            lineHeight: '14px',
            color: '#222222',
          }}
        >
          ©2025 Builduplabs Inc. All rights reserved.
        </Typography>
      </Box>
      {/* </Container> */}
    </FooterRoot >
  );
}

